import { useEffect } from 'react';
import {
  RouterProvider,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { DevTools as JotaiDevTools } from 'jotai-devtools';
import 'jotai-devtools/styles.css';
import { queryClient } from '~/apis';
import pkg from '../package.json';
import { MARQ_AI_CLIENT_ROUTER } from './router';
import LoadingCircularProgress from './components/LoadingCircularProgress';

Sentry.init({
  dsn: import.meta.env.REACT_APP_SENTRY_DSN_KEY,
  environment: import.meta.env.REACT_APP_ENV,
  integrations: [
    new Sentry.BrowserTracing({
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
    new Sentry.Replay({
      // Additional SDK configuration goes in here, for example:
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  release: pkg.version,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: ['production'].includes(import.meta.env.REACT_APP_ENV ?? '') ? 0.1 : 0,
  sampleRate: ['production', 'pre-release', 'staging'].includes(import.meta.env.REACT_APP_ENV ?? '') ? 1.0 : 0,
});

const USE_MSW = import.meta.env.MODE === 'development' && import.meta.env.REACT_APP_MSW === 'true';
const USE_JOTAI_DEVTOOLS =
  import.meta.env.MODE === 'development' && import.meta.env.REACT_APP_JOTAI_DEVTOOLS === 'true';

async function deferRender() {
  if (!USE_MSW) {
    return;
  }

  const { worker } = await import('./mocks/browser');

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start({
    onUnhandledRequest: 'bypass',
  });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLDivElement);

deferRender().then(() => {
  root.render(
    <>
      <QueryClientProvider client={queryClient}>
        <RouterProvider
          router={MARQ_AI_CLIENT_ROUTER}
          // opscc > client view 진입시 loader 로직 수행 중에 로딩 표시
          fallbackElement={<LoadingCircularProgress open />}
          future={{ v7_startTransition: true }}
        />
        {USE_JOTAI_DEVTOOLS && <JotaiDevTools />}
      </QueryClientProvider>
    </>
  );
});
